import { PlayCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { App, Button, Card, Descriptions, Space, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { loadPackingVideo } from "../warehouse/agent";
import { VideoOnDevice } from "../warehouse/VideoOnDevice";

/**
 * The warehouse's parcel of an order (WMS): how it was packed, and the
 * packing video for complaints while it is kept (Plan §6.9).
 */
export function ParcelCard({ orderId }: { orderId: string }) {
  const { t, formatDateTime, formatNumber } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const tz = me?.workspace?.timezone;
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const q = useQuery({
    queryKey: ["packing", "order", orderId],
    queryFn: () => unwrap(api.GET("/v1/packing/orders/{orderID}", { params: { path: { orderID: orderId } } })),
  });
  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);
  const parcel = q.data?.parcel;
  if (!parcel) return null;
  const video = parcel.video;
  const play = async () => {
    setLoading(true);
    try {
      setUrl(URL.createObjectURL(await loadPackingVideo(orderId, video?.location)));
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <Card title={t("orders.parcel")} size="small">
      <Descriptions column={1} size="small">
        <Descriptions.Item label={t("wms.pack.box")}>
          <span className="z-num">{parcel.packagingCode || "—"}</span>
          {parcel.suggestedCode && parcel.suggestedCode !== parcel.packagingCode && (
            <Typography.Text type="secondary"> · {t("orders.parcelSuggested", { code: parcel.suggestedCode })}</Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("wms.mf.weight")}>{formatNumber(parcel.weightG)} g</Descriptions.Item>
        <Descriptions.Item label={t("orders.parcelPacked")}>
          {parcel.packedBy} · {formatDateTime(parcel.packedAt, tz)}
        </Descriptions.Item>
        {parcel.deskName && (
          <Descriptions.Item label={t("wms.desk.packedAt")}>
            {parcel.deskName}
            {parcel.startedAt && ` · ${formatDateTime(parcel.startedAt, tz)} – ${formatDateTime(parcel.packedAt, tz)}`}
          </Descriptions.Item>
        )}
        {parcel.handedAt && <Descriptions.Item label={t("wms.mf.handedAt")}>{formatDateTime(parcel.handedAt, tz)}</Descriptions.Item>}
      </Descriptions>
      <div style={{ marginTop: 12 }}>
        {video ? (
          url ? (
            <video src={url} controls autoPlay style={{ width: "100%", borderRadius: 4, background: "black" }} />
          ) : (
            <Space orientation="vertical" size={4}>
              <Button icon={<PlayCircleOutlined />} loading={loading} onClick={() => void play()}>
                {t("orders.playVideo")}
              </Button>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("orders.videoKept", { at: formatDateTime(video.expiresAt, tz) })}
              </Typography.Text>
              {video.location === "device" && <VideoOnDevice orderId={orderId} requested={video.uploadRequested} />}
            </Space>
          )
        ) : (
          <Tag>{t("orders.noVideo")}</Tag>
        )}
      </div>
    </Card>
  );
}
